/**
 * One Piece set names, held in the browser.
 *
 * The bundled catalogue carries a code and a card name and nothing else, so
 * "the time of battle luffy" matched nothing while OP16 was sitting in every
 * one of those cards' ids. These are the missing half.
 *
 * Kept in local storage rather than fetched each time, because the whole point
 * of the One Piece search is that it answers as you type off a catalogue that
 * is already here. One request a week, and a stale copy is still a working
 * search — a set list that is a month old only fails to know about a set that
 * did not exist a month ago.
 */

const KEY = "ppt_op_sets_v1";
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

let memo = null;      // one parse per page, not one per keystroke

const read = () => {
  try {
    const raw = window.localStorage.getItem(KEY);
    const held = raw ? JSON.parse(raw) : null;
    if (held && held.sets && typeof held.at === "number") return held;
  } catch {
    /* a corrupt copy is no copy */
  }
  return null;
};

/** Whatever is known right now, without waiting. `{}` until the first load. */
export function knownSets() {
  if (memo) return memo;
  const held = read();
  memo = held?.sets || {};
  return memo;
}

/**
 * Make sure the names are here, fetching only if they are missing or old.
 *
 * Never throws and never leaves the caller waiting on a failure: the search
 * works by card name and by set code with no names at all, so a source that is
 * down costs one kind of query rather than the feature.
 */
export async function loadSets() {
  const held = read();
  if (held && Date.now() - held.at < TTL_MS) {
    memo = held.sets;
    return memo;
  }

  try {
    const res = await fetch("/api/op/sets");
    if (!res.ok) throw new Error(String(res.status));
    const { sets } = await res.json();
    if (sets && Object.keys(sets).length) {
      memo = sets;
      try {
        window.localStorage.setItem(KEY, JSON.stringify({ at: Date.now(), sets }));
      } catch {
        /* a blocked store means fetching again next visit, not failing now */
      }
      return memo;
    }
  } catch {
    /* fall through to whatever is already held */
  }

  // An old copy beats none, and none beats an error.
  memo = held?.sets || {};
  return memo;
}
